import { animate, state, style, transition, trigger } from '@angular/animations';
import { environment } from 'environments/environment';
import { Component, OnInit, ViewChild, ElementRef, ViewEncapsulation, HostListener } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AuthService } from 'app/core/authentication/authentication.service';
import { AboutService } from 'app/core/services/about.service';
import { ClientSwitchService } from 'app/core/services/client-switch.service';
import { DataStorageService } from 'app/core/services/data-storage.service';
import { DialogPassConfirmService } from 'app/core/services/dialog-pass-confirm.service';
import { DialogRemittSsoService } from 'app/core/services/dialog-remitt-sso.service';
import { ThemeService, AppTheme } from 'app/core/services/theme.service';
import { Account } from 'app/shared/models/account';
import { TourService } from 'app/core/services/tour.service';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

declare interface RouteInfo {
  path: string;
  title: string;
  icon: string;
  class: string;
}

export const ROUTES: RouteInfo[] = [
  { path: '/home', title: 'Home', icon: 'design_app', class: '' },
  { path: '/dashboard', title: 'Dashboard', icon: 'business_chart-bar-32', class: '' },
  { path: '/time-management', title: 'Time Management', icon: 'ui-1_calendar-60', class: '' },
  { path: '/employee-attendance', title: 'Attendance', icon: 'ui-1_calendar-60', class: '' },
  { path: '/schedules', title: 'Schedule', icon: 'ui-2_time-alarm', class: '' },
];

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  animations: [
    trigger('expandCollapse', [
      state('expanded', style({
        maxHeight: '2000px',
        overflow: 'visible'
      })),
      state('collapsed', style({
        maxHeight: '0',
        overflow: 'hidden'
      })),
      transition('expanded <=> collapsed', [
        animate('0.3s cubic-bezier(0.4, 0, 0.2, 1)')
      ])
    ])
  ]
})

export class SidebarComponent implements OnInit {
  @ViewChild('clientSearchInput') clientSearchInput: ElementRef;

  logoSidebar = environment.logoSidebar;
  logoAlt = environment.logoAlt;
  logoClass = environment.logoClass;

  menuItems: any[];
  accountInfo: Account;
  isSubmenuOpen: boolean = false;
  isSubmenuOpen2: boolean = false;
  isSubmenuOpenConfig: boolean = false;
  isProcessOpenConfig: boolean = false;
  isSubmenuOpenReport: boolean = false;
  isOperationsReportOpen: boolean = false;
  isComplianceReportOpen: boolean = false;
  initialData: any;
  isPlanExpired: boolean = true;
  isFormOpenConfig: boolean = false;
  isPayrollOpenConfig: boolean = false;
  isWorkflowOpenConfig: boolean = false;

  searchText: string = '';
  selectedClient: string = '';
  clientSearchText: string = '';
  filteredClients: any[] = [];

  tempSelectedClient: string = '';
  hasSelectedClient: boolean = false;

  isLoadingClients: boolean = false;
  isSwitchingClient: boolean = false;
  clientList = [];

  originalMenuItems = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      icon: 'grid_view',
      class: ''
    },
    {
      title: 'Home',
      path: '/home',
      icon: 'home',
      class: ''
    },
    {
      title: 'Time Management',
      path: '/time-management',
      icon: 'calendar_today',
      class: ''
    },
    {
      title: 'Attendance',
      path: '/employee-attendance',
      icon: 'event_available',
      class: ''
    },
    {
      title: 'My Schedule',
      path: '/schedules',
      icon: 'schedule',
      class: ''
    },
  ];

  employeeSubmenuItems = [
    { title: 'Dashboard', path: '/employee-dashboard', icon: 'bar_chart', condition: () => this.accountInfo?.supervisorMode },
    { title: 'Attendance', path: '/employee-attendance', icon: 'calendar_today', condition: () => this.accountInfo?.supervisorMode },
    { title: 'Management', path: '/employee-management', icon: 'manage_accounts', condition: () => this.accountInfo?.supervisorMode || this.accountInfo?.isSuperUser },
    { title: 'Schedule Range', path: '/default-schedule', icon: 'schedule', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Employee Schedule', path: '/employee-schedule/0', icon: 'event_note', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Change Schedule', path: '/employee-workdays-requests', icon: 'schedule', condition: () => this.accountInfo?.supervisorMode || this.accountInfo?.isSuperUser },
    { title: 'Manpower Planning', path: '/shift-planner', icon: 'event_note', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode, noAccessCheck: true },
    { title: 'Leaves', path: '/employee-leaves', icon: 'send', condition: () => this.accountInfo?.supervisorMode || this.accountInfo?.isSuperUser },
    { title: 'Reimbursement', path: '/expense', icon: 'account_balance_wallet', condition: () => this.accountInfo?.supervisorMode },
    { title: 'Biometric Verification', path: '/employee-timeEntries', icon: 'person', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Devices', path: '/employee-devices', icon: 'devices', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
  ];

  payrollSubmenuItems = [
    {
      title: 'Time Entries',
      path: '/personal-time-entries',
      icon: 'pending_actions',
      condition: () => !this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    },
    {
      title: 'Timesheet',
      path: '/personal-timesheet',
      icon: 'pending_actions',
      condition: () => !this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    },
    {
      title: 'Payroll',
      path: '/personal-payroll',
      icon: 'payments',
      condition: () => this.accountInfo?.isAllowPayroll
    }
  ];

  payrollExpensesSubmenuItems = [
    { title: 'Time Entries', path: '/time-entries', icon: 'schedule', condition: () => this.accountInfo?.supervisorMode || this.accountInfo?.isSuperUser },
    { title: 'Timesheet', path: '/employee-timeSheets', icon: 'pending_actions', condition: () => this.accountInfo?.supervisorMode || this.accountInfo?.isSuperUser },
    {
      title: 'Generate Employee Fixed Timesheet',
      path: '/generate-employee-fixed-timesheet',
      icon: 'settings',
      condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    },
    {
      title: 'Batch Process Timesheet',
      path: '/batch-process-timesheet',
      icon: 'dynamic_feed',
      condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    },
    { title: 'Payroll Process', path: '/payroll-process', icon: 'payments', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Payroll Movement', path: '/payroll-movement', icon: 'payments', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    {
      title: 'Generate Payroll',
      path: '/generate-payroll',
      icon: 'settings',
      condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    },
    {
      title: 'Employee Payroll',
      path: '/employee-payroll',
      icon: 'payments',
      condition: () => this.accountInfo?.supervisorMode || this.accountInfo?.isSuperUser
    },
    {
      title: 'Loans',
      path: '/loans',
      icon: 'account_balance',
      condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    },
    {
      title: 'Reimbursement',
      path: '/expense',
      icon: 'account_balance_wallet',
      condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    },
    {
      title: 'Deductions',
      path: '/deductions',
      icon: 'tune',
      condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    },
    {
      title: 'Adjustments',
      path: '/payroll-adjustments',
      icon: 'tune',
      condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    }
  ];

  payrollSetupSubmenuItems = [
    { title: 'Access Management', path: '/access-management', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Account Element', path: '/account-element', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Charge', path: '/payroll-charge', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Dep. / Branch / Job', path: '/payroll-department', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Holiday', path: '/holidays', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Payroll Concept', path: '/payroll-concept', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Payroll Definition', path: '/payroll-definition', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Salary Scale', path: '/salary-scale', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Clients', path: '/client-management', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'System Config.', path: '/system-config', icon: 'settings', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode }
  ];

  operationsReportsItems = [
    { title: 'Work Attendance Log', path: '/work-attendance-log', icon: 'event_note', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Time Tracking Comparison', path: '/time-tracking-comparison', icon: 'compare_arrows', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Time Entries Summary', path: '/time-entries-summary', icon: 'access_time', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Timesheet Summary', path: '/timesheet-summary', icon: 'pending_actions', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Leave Summary', path: '/leave-report', icon: 'event_available', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Payroll Summary', path: '/payroll-summary', icon: 'payments', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Holiday Summary', path: '/holidays-report', icon: 'celebration', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Late Report', path: '/late-report', icon: 'schedule', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Undertime Report', path: '/undertime-report', icon: 'timelapse', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Invalid Timesheet', path: '/invalid-timesheet', icon: 'warning', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
  ];

  complianceReportsItems = [
    { title: 'SSS Alphalist', path: '/sss-alphalist-report', icon: 'description', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'PhilHealth Alphalist', path: '/philhealth-alphalist-report', icon: 'description', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Pag-IBIG Alphalist', path: '/pagibig-alphalist-report', icon: 'description', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Taxes Withheld', path: '/taxes-withheld-report', icon: 'receipt_long', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
    { title: 'Employee DTR', path: '/employee-dtr', icon: 'today', condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode },
  ];

  formsSubmenuItems = [
    { title: 'Change Schedule', path: '/workdays-requests', icon: 'schedule', condition: () => !this.accountInfo?.isSuperUser },
    { title: 'Leave Form', path: '/apply-leave', icon: 'send', condition: () => !this.accountInfo?.isSuperUser },
    { title: 'Reimbursement Form', path: '/reimbursement-form', icon: 'payments', condition: () => !this.accountInfo?.isSuperUser },
    { title: 'Loan Form', path: '/loan-request', icon: 'real_estate_agent', condition: () => !this.accountInfo?.isSuperUser }
  ];

  scheduledProcessSubmenuItems = [
    { title: 'Time Entries Process', path: '/process-time-entries', icon: 'settings', condition: () => this.accountInfo?.isSuperUser },
    { title: 'Timesheet Process', path: '/process-timesheet', icon: 'settings', condition: () => this.accountInfo?.isSuperUser },
    { title: 'Payroll Invoice', path: '/process-payroll-invoice', icon: 'settings', condition: () => this.accountInfo?.isSuperUser }
  ];

  workflowSubmenuItems = [
    {
      title: 'Payroll Transactions',
      path: '/payroll-transactions',
      icon: 'account_tree',
      condition: () => this.accountInfo?.isSuperUser
    }
  ];

  standaloneNavItems = [
    {
      title: 'Support Ticket',
      path: '/support-ticket',
      icon: 'handyman',
      id: 'support',
      condition: () => this.accountInfo?.isSuperUser || this.accountInfo?.supervisorMode
    }
  ];

  showFavoritesOnly: boolean = false;
  favorites: Set<string> = new Set();

  showThemePicker: boolean = false;
  get themes(): AppTheme[] { return this.themeService.themes; }
  get currentThemeId(): string { return this.themeService.currentThemeId; }

  isClientDropdownOpen: boolean = false;
  isOverlayDropdownOpen: boolean = false;

  allowedMenus: string[] = [];
  isSubServiceProvider: boolean = false;
  isLoggedInAsSPClient: boolean = false;
  private sidebarScrollPosition = 0;
  @ViewChild('sidebarWrapper', { static: false }) sidebarWrapper: ElementRef;

  constructor(
    private router: Router,
    private about: AboutService,
    private remittServiceDialog: DialogRemittSsoService,
    private dataStorage: DataStorageService,
    private dialogPassConfirmService: DialogPassConfirmService,
    private clientSwitchService: ClientSwitchService,
    private authService: AuthService,
    public themeService: ThemeService,
    private tourService: TourService) { }

  ngOnInit() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.sidebarWrapper?.nativeElement) {
          this.sidebarScrollPosition = this.sidebarWrapper.nativeElement.scrollTop;
        }
      }

      if (event instanceof NavigationEnd) {
        if (this.sidebarWrapper?.nativeElement) {
          this.sidebarWrapper.nativeElement.scrollTop = this.sidebarScrollPosition;
        }
      }
    });

    this.menuItems = ROUTES.filter(menuItem => menuItem);

    const supervisorMode = localStorage.getItem('account');
    this.isSubServiceProvider = JSON.parse(localStorage.getItem('IsSubServiceProvider'));
    this.isLoggedInAsSPClient = JSON.parse(localStorage.getItem('isLoggedInAsSPClient'));

    if (supervisorMode) {
      this.accountInfo = JSON.parse(supervisorMode);

      // Initialize selectedClient from accountInfo.clientId
      if (this.accountInfo.clientId) {
        this.selectedClient = this.accountInfo.clientId;
        this.tempSelectedClient = this.accountInfo.clientId;
        this.hasSelectedClient = true;
      }
    }

    const menuAccessData = localStorage.getItem('myMenuAccess');
    if (menuAccessData) {
      const parsed = JSON.parse(menuAccessData);
      this.allowedMenus = parsed.map((m: any) => m.title);
    }

    if (this.accountInfo?.isServiceProvider || this.accountInfo?.isServiceProviderClient) {
      this.loadClientsFromAPI();
    }

    // Subscribe to current client changes
    this.clientSwitchService.currentClient$.subscribe(client => {
      if (client) {
        // Always prioritize accountInfo.clientId over currentClient
        if (this.accountInfo?.clientId && client.id !== this.accountInfo.clientId) {
          this.selectedClient = this.accountInfo.clientId;
          this.tempSelectedClient = this.accountInfo.clientId;
        } else {
          this.selectedClient = client.id;
          this.tempSelectedClient = client.id;
        }
        this.hasSelectedClient = true;
      }
    });

    // Sync check: Ensure selectedClient matches accountInfo.clientId
    if (this.accountInfo?.clientId) {
      this.selectedClient = this.accountInfo.clientId;
      this.tempSelectedClient = this.accountInfo.clientId;
    }

    this.checkActiveRoute();
    this.checkActiveRoute2();
    this.checkActiveRoute3();
    this.checkActiveRouteProcess();
    this.checkActiveRoute4();
    this.checkActiveRoute5();
    this.checkActiveRoute6();
    this.checkActiveRoute7();

    this.initialData = this.dataStorage.getInitialData();
    this.loadFavorites();
  }

  ngAfterViewInit() {
    this.filterClients();

    // Final sync check after view initialization
    if (this.accountInfo?.clientId && this.selectedClient !== this.accountInfo.clientId) {
      this.selectedClient = this.accountInfo.clientId;
      this.tempSelectedClient = this.accountInfo.clientId;
    }
  }

  loadClientsFromAPI(): void {
    this.isLoadingClients = true;
    this.clientSwitchService.getAvailableClients().subscribe(
      response => {
        this.clientList = response.clients;
        this.filteredClients = [...this.clientList];
        this.isLoadingClients = false;
      },
      error => {
        console.error('Error loading clients:', error);
        this.isLoadingClients = false;
        this.clientList = [];
        this.filteredClients = [];
      }
    );
  }

  onDropdownOpenChange(isOpen: boolean) {
    if (isOpen) {
      this.clientSearchText = '';
      this.filteredClients = [...this.clientList];

      setTimeout(() => {
        if (this.clientSearchInput) {
          this.clientSearchInput.nativeElement.focus();
        }
      }, 100);
    }
  }

  filterClients(): void {
    if (!this.clientSearchText) {
      this.filteredClients = [...this.clientList];
    } else {
      const searchLower = this.clientSearchText.toLowerCase();
      this.filteredClients = this.clientList.filter(client =>
        client.name.toLowerCase().includes(searchLower) ||
        client.id.toLowerCase().includes(searchLower)
      );
    }
  }

  getClientName(clientId: string): string {
    const client = this.clientList.find(c => c.id === clientId);
    return client ? client.name : '';
  }

  onClientChange(event: any): void {
    const newClientId = event.value;
    console.log('Selected client ID:', newClientId);
    if (this.accountInfo.isSubServiceProvider) {
      localStorage.setItem('IsSubServiceProvider', JSON.stringify(true));
    }
    if (newClientId && newClientId !== this.accountInfo?.clientId) {
      this.isSwitchingClient = true;

      // Store the selected clientId temporarily
      const tempClientId = newClientId;

      this.clientSwitchService.switchClient(newClientId).pipe(
        switchMap(response => {
          console.log('Switch client response:', response);
          // Call getAccount after successful client switch
          return this.authService.getAccount();
        })
      ).subscribe(
        accountResponse => {
          console.log('Account response:', accountResponse);

          // Preserve the clientId in the account response
          accountResponse.clientId = tempClientId;

          // Update localStorage with new account data including clientId
          localStorage.setItem('account', JSON.stringify(accountResponse));

          // Update component state
          this.accountInfo = accountResponse;
          this.selectedClient = tempClientId;

          console.log('Successfully switched to client:', tempClientId);

          // Navigate based on user type
          if (accountResponse.isSuperUser) {
            this.router.navigate(['dashboard']).then(() => {
              window.location.reload();
            });
          } else {
            this.router.navigate(['home']).then(() => {
              window.location.reload();
            });
          }
        },
        error => {
          console.error('Error switching client or getting account:', error);
          this.isSwitchingClient = false;

          // Revert the selection in the dropdown
          this.selectedClient = this.accountInfo?.clientId || '';

          alert('Failed to switch client. Please try again.');
        }
      );
    }
  }

  getFilteredMainItems() {
    const excludedPaths = [
      'employee-devices', 'employee-timeEntries', 'employee-timeSheets', 'employee-payroll',
      'payroll-summary', 'timesheet-summary', 'loans', 'expense', 'employee-management', 'employee-leaves',
      'payroll-definition', 'payroll-concept', 'holidays', 'employee-summary', 'holidays-report',
      'payroll-periods-report', 'system-config', 'salary-scale', 'payroll-charge'
    ];
    const lowerSearch = this.searchText.toLowerCase();

    let result = this.originalMenuItems.filter(item => {
      const pathEnd = item.path.split('/').pop();
      const shouldExclude =
        excludedPaths.includes(pathEnd) ||
        (item.path === '/dashboard' && !this.accountInfo?.isSuperUser) ||
        (item.path === '/home' && this.accountInfo?.isSuperUser) ||
        (item.path === '/apply-leave' && this.accountInfo?.isSuperUser) ||
        (item.path === '/employee-attendance' && !this.accountInfo?.isSuperUser) ||
        (item.path === '/loan-request' && this.accountInfo?.isSuperUser) ||
        (item.path === '/reimbursement-form' && this.accountInfo?.isSuperUser) ||
        (item.path === '/schedules' && this.accountInfo?.isSuperUser) ||
        (item.path === '/time-management' &&
          ((this.accountInfo?.isSuperUser && !this.accountInfo?.isAllowClient) ||
            (!this.accountInfo?.isSuperUser && !this.accountInfo?.isAllowClient))
        );

      return !shouldExclude && item.title.toLowerCase().includes(lowerSearch);
    });

    if (this.showFavoritesOnly) {
      result = result.filter(item => this.isFavorite(item.title));
    }
    return result;
  }

  getFilteredSubmenuItemsDefault(items: any[]) {
    const search = this.searchText.toLowerCase();
    let result = items.filter(item => item.title.toLowerCase().includes(search) && item.condition());
    if (this.showFavoritesOnly) {
      result = result.filter(item => this.isFavorite(item.title));
    }
    return result;
  }

  getFilteredSubmenuItems(items: any[]) {
    const lowerSearch = this.searchText.toLowerCase();

    let result = items.filter(item => {
      const titleMatch = item.title.toLowerCase().includes(lowerSearch);

      if (this.accountInfo?.isSuperUser) {
        return item.condition() && titleMatch;
      }

      if (this.accountInfo?.supervisorMode) {
        const passAccess = item.noAccessCheck || this.isInMyMenuAccess(item.title);
        return item.condition() && passAccess && titleMatch;
      }

      return item.condition() && titleMatch;
    });

    if (this.showFavoritesOnly) {
      result = result.filter(item => this.isFavorite(item.title));
    }
    return result;
  }

  isInMyMenuAccess(title: string): boolean {
    return this.allowedMenus.includes(title);
  }

  isOverdue(date: string | Date): boolean {
    if (!date) return false;
    const today = new Date();
    const due = new Date(date);
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return due < today;
  }

  checkActiveRoute(): void {
    this.router.events.subscribe(() => {
      const openRoutes = [
        '/#/employee-dashboard',
        '/#/employee-attendance',
        '/#/employee-management',
        '/#/employee-workdays-requests',
        '/#/employee-leaves',
        '/#/employee-timeSheets',
        '/#/employee-timeEntries',
        '/#/time-entries',
        '/#/employee-devices',
        '/#/expense',
        '/#/default-schedule',
        '/#/timesheet-summary',
        '/#/employee-schedule',
      ];

      this.isSubmenuOpen = openRoutes.some(path => this.router.url.includes(path));
    });
  }

  checkActiveRoute2(): void {
    this.router.events.subscribe((event) => {
      if (this.router.url.includes('/#/employee-payroll') ||
        this.router.url.includes('/#/payroll-summary') ||
        this.router.url.includes('/#/loans') ||
        this.router.url.includes('/#/expense') ||
        this.router.url.includes('/#/deductions') ||
        this.router.url.includes('/#/payroll-adjustments') ||
        this.router.url.includes('/#/generate-payroll') ||
        this.router.url.includes('/#/payroll-process') ||
        this.router.url.includes('/#/payroll-movement') ||
        this.router.url.includes('/#/generate-employee-fixed-timesheet') ||
        this.router.url.includes('/#/batch-process-timesheet')) {
        this.isSubmenuOpen2 = true;
      } else {
        this.isSubmenuOpen2 = false;
      }
    });
  }

  checkActiveRoute3(): void {
    this.router.events.subscribe((event) => {
      if (this.router.url.includes('/#/payroll-definition')) {
        this.isSubmenuOpenConfig = true;
      } else {
        this.isSubmenuOpenConfig = false;
      }
    });
  }

  checkActiveRouteProcess(): void {
    this.router.events.subscribe((event) => {
      if (this.router.url.includes('/#/process-time-entries')) {
        this.isProcessOpenConfig = true;
      } else {
        this.isProcessOpenConfig = false;
      }
    });
  }

  checkActiveRoute4(): void {
    this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd)) return;

      const operationsRoutes = [
        '/work-attendance-log',
        '/time-entries-summary',
        '/timesheet-summary',
        '/leave-report',
        '/payroll-summary',
        '/holidays-report',
        '/late-report',
        '/undertime-report',
        '/time-tracking-comparison',
      ];
      const complianceRoutes = [
        '/sss-alphalist-report',
        '/philhealth-alphalist-report',
        '/pagibig-alphalist-report',
        '/taxes-withheld-report',
      ];

      const inOperations = operationsRoutes.some(p => this.router.url.includes(p));
      const inCompliance = complianceRoutes.some(p => this.router.url.includes(p));

      if (inOperations || inCompliance) {
        this.isSubmenuOpenReport = true;
        this.isOperationsReportOpen = inOperations;
        this.isComplianceReportOpen = inCompliance;
      } else {
        this.isSubmenuOpenReport = false;
      }
    });
  }

  checkActiveRoute5(): void {
    this.router.events.subscribe((event) => {
      if (this.router.url.includes('/#/loan-request')) {
        this.isFormOpenConfig = true;
      } else {
        this.isFormOpenConfig = false;
      }
    });
  }

  checkActiveRoute6(): void {
    this.router.events.subscribe((event) => {
      if (this.router.url.includes('/#/personal-time-entries')) {
        this.isPayrollOpenConfig = true;
      } else {
        this.isPayrollOpenConfig = false;
      }
    });
  }

  checkActiveRoute7(): void {
    this.router.events.subscribe((event) => {
      if (this.router.url.includes('/#/payroll-transactions')) {
        this.isWorkflowOpenConfig = true;
      } else {
        this.isWorkflowOpenConfig = false;
      }
    });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isClientDropdownOpen = false;
    this.isOverlayDropdownOpen = false;
    this.showThemePicker = false;
  }

  toggleThemePicker(event: Event): void {
    event.stopPropagation();
    this.showThemePicker = !this.showThemePicker;
  }

  applyTheme(themeId: string): void {
    this.themeService.applyTheme(themeId);
  }

  toggleClientDropdown(event: Event): void {
    event.stopPropagation();
    if (this.isSwitchingClient) return;
    this.isClientDropdownOpen = !this.isClientDropdownOpen;
    if (this.isClientDropdownOpen) {
      this.clientSearchText = '';
      this.filterClients();
      setTimeout(() => { if (this.clientSearchInput) this.clientSearchInput.nativeElement.focus(); }, 50);
    }
  }

  selectClientFromDropdown(clientId: string): void {
    this.isClientDropdownOpen = false;
    this.onClientChange({ value: clientId } as any);
  }

  toggleOverlayDropdown(event: Event): void {
    event.stopPropagation();
    this.isOverlayDropdownOpen = !this.isOverlayDropdownOpen;
    if (this.isOverlayDropdownOpen) {
      this.clientSearchText = '';
      this.filterClients();
    }
  }

  selectOverlayClient(clientId: string): void {
    this.tempSelectedClient = clientId;
    this.isOverlayDropdownOpen = false;
  }

  loadFavorites(): void {
    const stored = localStorage.getItem('sidebarFavorites');
    if (stored) {
      try { this.favorites = new Set(JSON.parse(stored)); } catch { this.favorites = new Set(); }
    }
    this.showFavoritesOnly = localStorage.getItem('sidebarFavoritesOnly') === 'true';
  }

  saveFavorites(): void {
    localStorage.setItem('sidebarFavorites', JSON.stringify([...this.favorites]));
  }

  toggleFavorite(title: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.favorites.has(title)) {
      this.favorites.delete(title);
    } else {
      this.favorites.add(title);
    }
    this.saveFavorites();
  }

  isFavorite(title: string): boolean {
    return this.favorites.has(title);
  }

  toggleFavoritesMode(): void {
    this.showFavoritesOnly = !this.showFavoritesOnly;
    localStorage.setItem('sidebarFavoritesOnly', String(this.showFavoritesOnly));
  }

  hasAnyFavoritedOperations(): boolean {
    if (!this.showFavoritesOnly) return true;
    return this.getFilteredSubmenuItems(this.operationsReportsItems).length > 0;
  }

  hasAnyFavoritedCompliance(): boolean {
    if (!this.showFavoritesOnly) return true;
    return this.getFilteredSubmenuItems(this.complianceReportsItems).length > 0;
  }

  hasAnyFavoritedReports(): boolean {
    if (!this.showFavoritesOnly) return true;
    return this.hasAnyFavoritedOperations() || this.hasAnyFavoritedCompliance();
  }

  isMobileMenu() {
    if (window.innerWidth > 991) {
      return false;
    }
    return true;
  };

  toggleSubmenu2(): void {
    this.isSubmenuOpen2 = !this.isSubmenuOpen2;
  }

  toggleSubmenu(): void {
    this.isSubmenuOpen = !this.isSubmenuOpen;
  }

  toggleSubmenuConfiguration(): void {
    this.isSubmenuOpenConfig = !this.isSubmenuOpenConfig;
  }

  toggleProcessConfiguration(): void {
    this.isProcessOpenConfig = !this.isProcessOpenConfig;
  }

  toggleFormConfiguration(): void {
    this.isFormOpenConfig = !this.isFormOpenConfig;
  }

  toggleWorkflowConfiguration(): void {
    this.isWorkflowOpenConfig = !this.isWorkflowOpenConfig;
  }

  toggleSubmenuReport(): void {
    this.isSubmenuOpenReport = !this.isSubmenuOpenReport;
  }

  toggleOperationsReport(event: Event): void {
    event.stopPropagation();
    this.isOperationsReportOpen = !this.isOperationsReportOpen;
  }

  toggleComplianceReport(event: Event): void {
    event.stopPropagation();
    this.isComplianceReportOpen = !this.isComplianceReportOpen;
  }

  togglePayrollConfiguration(): void {
    this.isPayrollOpenConfig = !this.isPayrollOpenConfig;
  }

  aboutDialog() {
    const dialogData = {};

    this.about.openDialog(dialogData).subscribe((result) => {
      if (result) {
        console.log('Logout confirmed' + result);
      }
    });
  }

  startTour() {
    this.tourService.start([
      { elementId: 'sidebar',          title: 'Sidebar Navigation',  description: 'Navigate through all modules here. Use the search bar to jump to any page quickly.' },
      { elementId: 'dashboard',        route: '/dashboard', title: 'Dashboard',           description: 'Your command center — attendance, approvals, and payroll analytics at a glance.' },
      { elementId: 'tour-metrics',     route: '/dashboard', title: 'Attendance Summary',  description: "Today's present, late, absent, rest day, and total employee count." },
      { elementId: 'tour-approvals',   route: '/dashboard', title: 'Approval Requests',   description: 'Review and act on pending leave, loan, and change schedule requests.' },
      { elementId: 'tour-charts',      route: '/dashboard', title: 'Annual Statistics',   description: 'Charts for hours worked, overtime, salary computations, and lates & absences.' },
      { elementId: 'payroll-expense',  title: 'Payroll & Expense',  description: 'Process timesheets, payroll, loans, reimbursements, and deductions.' },
      { elementId: 'employee',         title: 'Employee',            description: 'Manage records, attendance, schedules, and leave requests for your team.' },
      { elementId: 'payroll-setup',    title: 'Payroll Setup',       description: 'Configure charges, salary scales, concepts, and payroll definitions.' },
    ]);
  }

  remittDialog() {
    const dialogData = {
      title: 'Connect your remittance account'
    };

    this.remittServiceDialog.openDialog(dialogData).subscribe((result) => {
      if (result) {
        console.log('Logout confirmed' + result);
      }
    });
  }

  confirmPassword(route: any) {
    const isPassConfirm = JSON.parse(localStorage.getItem('isPassConfirm'));

    const protectedRoutes: { [key: string]: { content: string; storageKey: string } } = {
      '/employee-management': { content: 'To use employee management you need to confirm your password.', storageKey: 'isPassConfirm' },
      '/access-management':   { content: 'To use access management you need to confirm your password.',  storageKey: 'isPassConfirm_access' },
    };

    const routeConfig = protectedRoutes[route];
    const routeKey = routeConfig?.storageKey;
    const isRouteConfirmed = routeKey ? localStorage.getItem(routeKey) === 'true' : true;

    if (!this.accountInfo.isServiceProviderClient && !isRouteConfirmed && routeConfig) {
      const dialogData = {
        title: 'Confirm Password',
        content: routeConfig.content,
        cancelText: 'Cancel',
        confirmText: 'Confirm',
        storageKey: routeConfig.storageKey,
        navigateTo: route.replace('/', ''),
      };

      this.dialogPassConfirmService.openDialog(dialogData).subscribe((result) => {
        if (result) {
          this.router.navigate([route]);
        }
      });
      return;
    }

    // For routes with routerLink, don't navigate here (routerLink handles it)
    if (!this.shouldNavigateDirectly(route)) {
      this.router.navigate([route]);
    }
  }

  shouldNavigateDirectly(route: string): boolean {
    if (this.accountInfo?.isServiceProviderClient) return true;

    const storageKeys: { [key: string]: string } = {
      '/employee-management': 'isPassConfirm',
      '/access-management':   'isPassConfirm_access',
    };

    const key = storageKeys[route];
    if (key && localStorage.getItem(key) !== 'true') return false;

    return true;
  }

  renewPlan() {
    this.router.navigate(['user-profile']);
  }

  openChatMIA() {
    this.router.navigate(['broadcast-message']);
  }

  confirmClientSelection(): void {
    if (this.tempSelectedClient && !this.isSwitchingClient) {
      this.isSwitchingClient = true;

      // Store the selected clientId temporarily
      const tempClientId = this.tempSelectedClient;

      this.clientSwitchService.switchClient(this.tempSelectedClient).pipe(
        switchMap(response => {
          console.log('Switch client response:', response);
          // Call getAccount after successful client switch
          return this.authService.getAccount();
        })
      ).subscribe(
        accountResponse => {
          console.log('Account response:', accountResponse);

          // Preserve the clientId in the account response
          accountResponse.clientId = tempClientId;

          // Update localStorage with new account data including clientId
          localStorage.setItem('account', JSON.stringify(accountResponse));

          // Update component state
          this.selectedClient = tempClientId;
          this.hasSelectedClient = true;
          this.accountInfo = accountResponse;

          console.log('Successfully switched to client:', tempClientId);

          // Navigate based on user type
          if (accountResponse.isSuperUser) {
            this.router.navigate(['dashboard']).then(() => {
              window.location.reload();
            });
          } else {
            this.router.navigate(['home']).then(() => {
              window.location.reload();
            });
          }
        },
        error => {
          console.error('Error switching client or getting account:', error);
          this.isSwitchingClient = false;

          alert('Failed to switch client. Please try again.');
        }
      );
    }
  }

  clearClientSelection(): void {
    if (confirm('Are you sure you want to switch back to your service provider account?')) {
      this.clientSwitchService.clearClientSelection();
      this.selectedClient = '';
      this.tempSelectedClient = '';
      this.hasSelectedClient = false;

      // Clear clientId from accountInfo
      if (this.accountInfo) {
        this.accountInfo.clientId = null;
        localStorage.setItem('account', JSON.stringify(this.accountInfo));
      }

      window.location.reload();
    }
  }

  isActingAsClient(): boolean {
    return this.clientSwitchService.isActingAsClient();
  }

  navigateToClientRegistration(): void {
    this.router.navigate(['client-registration']);
  }

  goBackToPreviousAccount(): void {
    if (!this.clientSwitchService.hasPreviousAccount()) {
      alert('No previous account to go back to.');
      return;
    }

    this.isSwitchingClient = true;

    const previousAccount = this.clientSwitchService.getPreviousAccount();
    console.log('Going back to previous account:', previousAccount);

    this.clientSwitchService.goBackToPreviousAccount().pipe(
      switchMap(response => {
        if (!response) {
          throw new Error('Failed to switch back');
        }
        console.log('Go back response:', response);
        return this.authService.getAccount();
      })
    ).subscribe({
      next: (accountResponse) => {
        console.log('Account response after going back:', accountResponse);

        // Update localStorage with account data
        localStorage.setItem('account', JSON.stringify(accountResponse));

        // Update component state
        this.accountInfo = accountResponse;
        this.selectedClient = accountResponse.clientId || '';

        console.log('Successfully switched back to previous account');

        // Remove IsSubServiceProvider flag
        localStorage.removeItem('IsSubServiceProvider');

        // Navigate based on user type (no leading slash needed)
        if (accountResponse.isSuperUser) {
          this.router.navigate(['/dashboard']).then(() => {
            window.location.reload();
          });
        } else {
          this.router.navigate(['/home']).then(() => {
            window.location.reload();
          });
        }
      },
      error: (error) => {
        console.error('Error switching back to previous account:', error);
        this.isSwitchingClient = false;
        alert('Failed to switch back to previous account. Please try again.');
      }
    });
  }


  /**
   * Check if there's a previous account available
   */
  hasPreviousAccount(): boolean {
    return this.clientSwitchService.hasPreviousAccount();
  }
}